import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Check, ArrowRight, Phone, Star } from "lucide-react";
import Seo, { localBusinessSchema, faqSchema } from "../components/site/Seo";
import Breadcrumbs from "../components/site/Breadcrumbs";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import FaqSection from "../components/site/FaqSection";
import FinalCTA from "../components/site/FinalCTA";
import { getLocation } from "../lib/locations";
import { SERVICES_DATA } from "../lib/services";
import { REVIEWS, PHONE_DISPLAY, PHONE_TEL } from "../lib/site";

const CityDetail = () => {
  const { slug } = useParams();
  const city = getLocation(slug);
  if (!city) return <Navigate to="/service-areas" replace />;

  const cityReviews = REVIEWS.filter((r) => r.city.toLowerCase().includes(city.name.toLowerCase())).slice(0, 2);
  const reviews = cityReviews.length ? cityReviews : REVIEWS.slice(0, 2);

  return (
    <div>
      <Seo
        title={`Garage Floor Coatings in ${city.name}, AZ | PRYME EPOXY`}
        description={`Premium garage, patio and commercial floor coatings in ${city.name}, Arizona. Licensed, bonded, insured with a lifetime warranty. Call Tyler for a free estimate.`}
        path={`/service-areas/${city.slug}`}
        jsonLd={localBusinessSchema()}
        jsonLdId="city-localbusiness"
      />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: city.name, path: `/service-areas/${city.slug}` }]} />

      <PageHero
        overline={`${city.primary ? "Primary" : ""} Service Area`}
        title={`Floor Coatings in ${city.name}, Arizona`}
        subtitle={city.intro[0]}
        image={city.image}
      />

      {/* Intro + conditions */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 text-left">
            <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Serving {city.name}</span>
            <h2 className="text-2xl md:text-3xl font-light text-white mt-2 mb-5">Local Service You Can Trust</h2>
            {city.intro.slice(1).map((p, i) => (
              <p key={i} className="text-zinc-300 text-lg leading-relaxed mb-4">{p}</p>
            ))}
            <div className="border-l-2 border-[#C69453] pl-5 mt-6">
              <h3 className="text-white font-semibold text-lg">Common {city.name} Garage Conditions</h3>
              <p className="text-zinc-400 mt-2 leading-relaxed">{city.conditions}</p>
            </div>
            {city.landmarks?.length ? (
              <div className="flex flex-wrap gap-2 mt-6">
                {city.landmarks.map((l) => (
                  <span key={l} className="flex items-center gap-1.5 border border-zinc-800 bg-[#141414] text-zinc-400 text-sm px-3 py-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#C69453]" />{l}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-800 bg-[#141414] p-7 relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />
              <h3 className="text-xl font-semibold text-white mb-4">Why {city.name} Chooses PRYME</h3>
              <ul className="space-y-3 text-zinc-300">
                {["Licensed, bonded & insured", "Residential lifetime warranty", "Premium industrial-grade materials", "Honest, transparent estimates", "Local — never a franchise"].map((b) => (
                  <li key={b} className="flex items-start gap-3"><Check className="w-5 h-5 text-[#C69453] shrink-0 mt-0.5" /><span>{b}</span></li>
                ))}
              </ul>
              <a href={PHONE_TEL} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C69453] hover:bg-[#AE7F44] text-black font-bold px-6 py-3.5 transition-colors">
                <Phone className="w-5 h-5" /><span>Call Pryme: {PHONE_DISPLAY}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services available (internal links) */}
      <section className="py-14 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">Services We Offer in {city.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES_DATA.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center justify-between border border-zinc-800 bg-[#141414] p-4 hover:border-[#C69453] transition-colors group">
                <span className="text-white font-medium">{s.name}</span>
                <ArrowRight className="w-4 h-4 text-[#C69453] group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local reviews */}
      <section className="py-14 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">What {city.name}-Area Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border border-zinc-800 bg-[#141414] p-7 h-full">
                  <div className="flex text-yellow-400 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}</div>
                  <p className="text-zinc-300 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                  <p className="text-white font-bold mt-4">{r.name} <span className="text-zinc-500 font-normal text-sm">— {r.city}</span></p>
                </div>
              </Reveal>
            ))}
          </div>
          <Link to="/reviews" className="inline-flex items-center gap-2 mt-6 text-[#C69453] font-semibold hover:gap-3 transition-all">
            <span>Read more reviews</span><ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Map */}
      <section className="py-14 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">Serving {city.name} &amp; Surrounding Areas</h2>
          <div className="border border-zinc-800 overflow-hidden aspect-[16/7]">
            <iframe
              title={`Map of ${city.name}, Arizona`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(city.name + ", Arizona")}&output=embed`}
              className="w-full h-full grayscale contrast-125"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <FaqSection faqs={city.faqs} heading={`${city.name} — Questions`} className="bg-[#0A0A0A]" />

      <FinalCTA heading={`Get Your Free ${city.name} Estimate Today`} />
      <Seo jsonLd={faqSchema(city.faqs)} jsonLdId="city-faq-schema" path={`/service-areas/${city.slug}`} />
    </div>
  );
};

export default CityDetail;
